import React, {useState} from 'react';
import {useDebounce} from '$modules/frontdesk/hooks';
import {usePatientListHook} from '../../../Calendar/OfflineBooking/hooks/usePatientListHook';
import FrontdeskSearchbox from '$components/FrontdeskSearchBox';
import SearchRefractionIcon from '$assets/svgs/search-refraction.svg?react';
import PatientInfoContainer from '../../../PatientList/PatientDetails/Overview/PatientInfo';
import Loading from '$components/Loading';
import {dateToDDMMYYYY} from '$utils/date';
import {Dialog, IconButton} from '@mui/material';
import {XCloseIcon} from '$assets/svgs';
import './style.scss';
import FrontdeskButton from '~/components/FrontdeskButton';
import {useNewPayment} from '~/hooks/useNewPayment';
import {useNewInsuranceClaim} from '../../../Finance/InsuranceClaims/hooks';
import {ArrowNarrowRightIcon} from '$assets/svgs';

type SearchPatientType = 'payment' | 'insurance_claim';

interface SearchPatientContainerProps {
  newAppointmentModalOpen: boolean;
  handleNewAppointmentModalOpen: () => void;
  type?: SearchPatientType;
}

const SearchPatientContainer: React.FC<SearchPatientContainerProps> = ({
  newAppointmentModalOpen,
  handleNewAppointmentModalOpen,
  type = 'payment',
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(
    null
  );
  const debouncedSearchText = useDebounce<string>(searchText, 500);
  const {patients, isPending} = usePatientListHook(debouncedSearchText);
  const {isLoading: isPaymentLoading, createPayment} = useNewPayment();
  const {isLoading: isClaimLoading, createInsuranceClaim} =
    useNewInsuranceClaim();

  const isLoading = isPaymentLoading || isClaimLoading;
  const isSearching = searchText && !selectedPatientId;
  const hasSelectedPatient = Boolean(selectedPatientId);

  const handleClick = (id: number, fullName: string) => {
    setSelectedPatientId(id);
    setSearchText(fullName);
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    if (selectedPatientId) {
      setSelectedPatientId(null);
    }
  };

  const handleConfirm = async () => {
    if (selectedPatientId) {
      if (type === 'payment') {
        await createPayment(selectedPatientId);
      } else if (type === 'insurance_claim') {
        await createInsuranceClaim(selectedPatientId);
      }
      handleNewAppointmentModalOpen();
    }
  };

  const getButtonTitle = () => {
    return type === 'payment' ? 'New Payment' : 'Submit a claim';
  };

  const highlightMatch = (fullName: string, searchText: string) => {
    if (!searchText) return fullName;

    const parts = fullName.split(new RegExp(`(${searchText})`, 'gi'));
    return (
      <span>
        {parts.map((part, index) =>
          part.toLowerCase() === searchText.toLowerCase() ? (
            <strong key={index}>{part}</strong>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  return (
    <Dialog
      open={newAppointmentModalOpen}
      className="offline-booking-dialog-container"
      maxWidth="lg"
      fullWidth
    >
      <div className="dialog-close">
        <IconButton onClick={handleNewAppointmentModalOpen}>
          <XCloseIcon />
        </IconButton>
      </div>
      <h2 className="dialog-title">Please select the patient</h2>
      <div className="dialog-body">
        <div className="search-patient-form-container">
          <div className="search-patient-dropdown">
            <FrontdeskSearchbox
              value={searchText}
              startIcon={<SearchRefractionIcon />}
              placeholder="Search patient"
              onChange={handleSearchTextChange}
            />
          </div>

          {isSearching && (
            <div className="search-patient-result-wrapper">
              {isPending ? (
                <Loading />
              ) : patients && patients.length > 0 ? (
                patients.map(patient => (
                  <div
                    key={patient.id}
                    className="search-patient-info-item"
                    onClick={() => handleClick(patient.id, patient.fullName)}
                  >
                    <div className="patient-name">
                      {highlightMatch(patient.fullName, searchText)}
                    </div>
                    <div className="patient-dob">
                      {dateToDDMMYYYY(patient.dob)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-data-found">No data found</div>
              )}
            </div>
          )}
        </div>

        {hasSelectedPatient && selectedPatientId && (
          <div className="patient-info-container">
            <PatientInfoContainer
              key={selectedPatientId}
              patientId={selectedPatientId}
              canEditPatient={false}
              canInvitePatient={false}
              canViewFullProfile={true}
            />
          </div>
        )}
      </div>
      <div className="dialog-footer">
        <FrontdeskButton
          variant="primary"
          title={getButtonTitle()}
          disabled={!hasSelectedPatient || isLoading}
          onClick={handleConfirm}
          endIcon={<ArrowNarrowRightIcon />}
          loading={isLoading}
        />
      </div>
    </Dialog>
  );
};

export default SearchPatientContainer;
